// shared/dashboardOrigination.ts
/**
 * The Operations dashboard's origination rules and payload shape, shared by
 * server/dashboardOrigination.ts (which computes it) and the widgets (which
 * render it). Everything here is derived at read time; nothing is stored.
 */
import { PROSPECT_STAGES } from "./prospectConstants";

/** Stages that put a prospect in the column on their own (Caleb, 2026-09-29). */
export const IN_PLAY_STAGES: readonly string[] = PROSPECT_STAGES.slice(PROSPECT_STAGES.indexOf("In conversation"));

/** MVA statuses meaning someone is building it; the other open statuses are waiting. */
export const MVA_WORKING: readonly string[] = ["In progress", "In review"];

export type PlayGroup = "proposed" | "mva" | "delivered" | "conversation" | "starred";
export const PLAY_GROUP_LABELS: Record<PlayGroup, string> = {
  proposed: "Engagement proposed",
  mva: "MVA open",
  delivered: "MVA delivered · next: propose engagement",
  conversation: "In conversation",
  starred: "Starred",
};
const GROUP_ORDER: PlayGroup[] = ["proposed", "mva", "delivered", "conversation", "starred"];

export interface PlayProspect {
  id: string;
  name: string;
  stage: string | null;
  /** Present only for viewers with the financials grant. */
  starred?: boolean;
  ownerName: string | null;
  mvaStatus: string | null;
  mvaAssignee: string | null;
  sourceType: string | null;
  lastTouchAt: string | null;
}

export function playGroupOf(p: Pick<PlayProspect, "stage" | "mvaStatus">): PlayGroup {
  if (p.stage === "Engagement proposed") return "proposed";
  if (p.mvaStatus) return "mva";
  if (p.stage === "MVA delivered") return "delivered";
  if (p.stage === "In conversation") return "conversation";
  return "starred";
}

export function groupProspects(rows: PlayProspect[]): { group: PlayGroup; label: string; rows: PlayProspect[] }[] {
  const byGroup = new Map<PlayGroup, PlayProspect[]>();
  for (const r of rows) {
    const g = playGroupOf(r);
    byGroup.set(g, [...(byGroup.get(g) ?? []), r]);
  }
  // ISO strings sort chronologically; "" (never touched) sorts last in descending order.
  const newestFirst = (a: PlayProspect, b: PlayProspect) =>
    (b.lastTouchAt ?? "").localeCompare(a.lastTouchAt ?? "") || a.name.localeCompare(b.name);
  return GROUP_ORDER.filter((g) => byGroup.has(g)).map((g) => ({
    group: g,
    label: PLAY_GROUP_LABELS[g],
    rows: [...byGroup.get(g)!].sort(newestFirst),
  }));
}

/** A prospect with no touch in 30 days is going cold, which is actionable. */
export function lastTouchLabel(iso: string | null, now: Date): { text: string; stale: boolean; never: boolean } {
  if (!iso) return { text: "never", stale: false, never: true };
  const days = Math.floor((now.getTime() - Date.parse(iso)) / 86_400_000);
  return { text: days <= 0 ? "today" : `${days}d`, stale: days > 30, never: false };
}

export type TouchKind = "email" | "meeting" | "call";
/** Activity type to chart series: conference counts as a meeting; a note rides with calls. */
export function touchKindOf(type: string): TouchKind {
  if (type === "email") return "email";
  if (type === "meeting" || type === "conference") return "meeting";
  return "call";
}

export interface InboundValuation { id: string; companyName: string | null; contactName: string | null; headline: string | null; submittedAt: string }
export interface InboundReply { id: string; companyName: string | null; ownerName: string | null; lastSentAt: string | null }

export interface OriginationPayload {
  /** 12 Monday keys (Central), oldest first. Every weekly array aligns to it. */
  weeks: string[];
  tiles: {
    newProspects30d: number;
    newProspectsWeekly: number[];
    inConversation: { total: number; touched30d: number };
    mvasOpen: { working: number; waiting: number };
    /** `starred` is present only for viewers with the financials grant. */
    mvaDelivered: { total: number; starred?: number };
    prospectTouches30d: number;
    inbound: { valuations: number; replies: number };
    coiTouches30d: { meetings: number; calls: number; emails: number };
    coiTouchesWeekly: number[];
    mandatesYtd: number;
  };
  prospectsInPlay: PlayProspect[];
  funnel: { byStage: Record<string, number>; noStage: number; parked: number };
  sources: { sourceType: string; prospects: number; clients: number }[];
  sourceCoverage: { withSource: number; total: number };
  touchesWeekly: Record<TouchKind, number[]>;
  inbound: { valuations: InboundValuation[]; replies: InboundReply[] };
}
